import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { FaBars, FaChevronDown, FaChevronUp, FaSignOutAlt, FaTimes } from 'react-icons/fa';
import AccountPanel from './account/AccountPanel';
import SecurityModal from './account/SecurityModal';
import { logout } from '../../js/logout';
import useAccountProfile from '../../hooks/useAccountProfile';
import useSecurityPasswordForm from '../../hooks/useSecurityPasswordForm';
import logo from '../../img/logo/Logo_FICSULLANA2.png';

const SIDEBAR_WIDTH_CLASS = 'md:ml-[15rem]';

function getInitialOpenGroups(menuItems) {
  return menuItems.reduce((acc, item) => {
    if (item.type === 'group') {
      acc[item.id] = Boolean(item.defaultOpen);
    }
    return acc;
  }, {});
}

function isPathActive(pathname, targetPath) {
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
}

function LinkItem({ item, pathname, onNavigate }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold tracking-wide transition',
          isActive || isPathActive(pathname, item.to)
            ? 'bg-white/15 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]'
            : 'text-red-100 hover:bg-white/10 hover:text-white'
        ].join(' ')
      }
    >
      <Icon className="shrink-0 text-[14px]" aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function GroupItem({ group, pathname, isOpen, onToggle, onNavigate }) {
  const Icon = group.icon;
  const groupIsActive = group.items.some((entry) => isPathActive(pathname, entry.to));

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        className={[
          'flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-semibold tracking-wide transition',
          groupIsActive ? 'bg-white/12 text-white' : 'text-red-100 hover:bg-white/10 hover:text-white'
        ].join(' ')}
      >
        <span className="flex items-center gap-3">
          <Icon className="shrink-0 text-[14px]" aria-hidden="true" />
          <span className="text-left leading-tight">{group.label}</span>
        </span>
        {isOpen ? <FaChevronUp className="text-[12px]" /> : <FaChevronDown className="text-[12px]" />}
      </button>

      {isOpen ? (
        <div className="space-y-1 pl-4">
          {group.items.map((child) => (
            <LinkItem key={child.to} item={child} pathname={pathname} onNavigate={onNavigate} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function RoleShell({ role, menuItems, extraOverlay = null }) {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => getInitialOpenGroups(menuItems));
  const { profile, loading: profileLoading, error: profileError, reload: reloadProfile } = useAccountProfile(role);
  const securityForm = useSecurityPasswordForm({ onPasswordUpdated: reloadProfile });

  useEffect(() => {
    setOpenGroups(getInitialOpenGroups(menuItems));
  }, [menuItems]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [sidebarOpen]);

  return (
    <div className="min-h-screen bg-slate-100">
      <button
        type="button"
        onClick={() => setSidebarOpen((prev) => !prev)}
        className="fixed left-4 top-4 z-40 rounded-xl bg-red-700 p-2 text-white shadow-lg shadow-red-900/30 transition hover:bg-red-800 md:hidden"
        aria-label="Abrir menú"
      >
        {sidebarOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
      </button>

      {sidebarOpen ? (
        <div
          className="fixed inset-0 z-30 bg-slate-950/40 backdrop-blur-[1px] md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      {extraOverlay}

      <aside
        ref={sidebarRef}
        className={[
          'sidebar-scroll fixed inset-y-0 left-0 z-40 flex w-[15rem] flex-col overflow-y-auto border-r border-red-950/30 bg-gradient-to-b from-red-700 via-red-700 to-red-800 text-white transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0'
        ].join(' ')}
      >
        <div className="border-b border-white/15 bg-white px-6 py-5">
          <img src={logo} alt="Logo FIC Sullana" className="mx-auto h-20 w-auto object-contain" />
        </div>

        <nav className="space-y-3 px-4 py-5">
          {menuItems.map((item) =>
            item.type === 'group' ? (
              <GroupItem
                key={item.id}
                group={item}
                pathname={location.pathname}
                isOpen={Boolean(openGroups[item.id])}
                onToggle={() =>
                  setOpenGroups((prev) => ({
                    ...prev,
                    [item.id]: !prev[item.id],
                  }))
                }
                onNavigate={() => setSidebarOpen(false)}
              />
            ) : (
              <LinkItem
                key={item.to}
                item={item}
                pathname={location.pathname}
                onNavigate={() => setSidebarOpen(false)}
              />
            )
          )}
        </nav>

        <div className="mt-auto border-t border-white/15 p-4">
          <AccountPanel
            profile={profile}
            loading={profileLoading}
            error={profileError}
            onOpenSecurity={securityForm.openModal}
            onOpenLogout={() => setShowConfirm(true)}
          />
        </div>
      </aside>

      <main className={`min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100 transition-all ${SIDEBAR_WIDTH_CLASS}`}>
        <div className="px-4 pb-6 pt-16 md:px-8 md:pt-8">
          <Outlet />
        </div>
      </main>

      {showConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-900">¿Estás seguro/a de cerrar sesión?</h3>
            <p className="mt-2 text-sm text-slate-600">Tu sesión actual se cerrará en este dispositivo.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="rounded-xl bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-300"
              >
                NO
              </button>
              <button
                type="button"
                onClick={logout}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                <span className="inline-flex items-center gap-2">
                  <FaSignOutAlt />
                  SÍ
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <SecurityModal
        open={securityForm.open}
        onClose={securityForm.closeModal}
        values={securityForm.form}
        errors={securityForm.errors}
        submitting={securityForm.submitting}
        onChange={securityForm.handleFieldChange}
        onSubmit={securityForm.handleSubmit}
      />
    </div>
  );
}